import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { QRCodeSVG } from 'qrcode.react';
import { creditsService, CashPaymentResponse } from '../api/services/creditsService';
import { CreditPackage } from '../api/services/adminService';
import { showToast } from '../utils/toast';

export default function Credits() {
  const { t } = useTranslation();
  const [packages, setPackages] = useState<CreditPackage[]>([]);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState<string | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<'card' | 'cash'>('card');
  const [qrData, setQrData] = useState<CashPaymentResponse | null>(null);
  const [qrStatus, setQrStatus] = useState<'PENDING' | 'COMPLETED' | 'FAILED'>('PENDING');
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    creditsService.getPackages()
      .then(pkgs => setPackages(pkgs.filter(p => p.isActive).sort((a, b) => a.sortOrder - b.sortOrder)))
      .catch(() => showToast.error(t('credits.failed_load')))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!qrData || qrStatus !== 'PENDING') return;
    pollingRef.current = setInterval(async () => {
      try {
        const result = await creditsService.checkPaymentStatus(qrData.paymentId);
        if (result.status === 'COMPLETED') {
          setQrStatus('COMPLETED');
          clearInterval(pollingRef.current!);
          showToast.success(t('credits.credits_added', { count: qrData.credits }));
          setTimeout(() => closeQrModal(), 2000);
        } else if (result.status === 'FAILED') {
          setQrStatus('FAILED');
          clearInterval(pollingRef.current!);
        }
      } catch {}
    }, 5000);
    return () => clearInterval(pollingRef.current!);
  }, [qrData, qrStatus]);

  const closeQrModal = () => {
    clearInterval(pollingRef.current!);
    setQrData(null);
    setQrStatus('PENDING');
  };

  const handlePurchase = async (pkg: CreditPackage) => {
    setPurchasing(pkg.id);
    try {
      if (paymentMethod === 'card') {
        const result = await creditsService.initiateCardPayment(pkg.id);
        if (result.paymentUrl) window.open(result.paymentUrl, '_blank');
        sessionStorage.setItem('pendingCreditPaymentId', result.paymentId);
      } else {
        const result = await creditsService.purchaseCreditsCash(pkg.id);
        setQrData(result);
        setQrStatus('PENDING');
      }
    } catch (err: any) {
      showToast.error(err?.message || t('common.error_try_again'));
    } finally {
      setPurchasing(null);
    }
  };

  if (loading) return <div className="text-center p-5"><div className="spinner-border text-primary"></div></div>;

  return (
    <div className="dashboard-credits" style={{ paddingBottom: 40, maxWidth: 1000, margin: '0 auto' }}>
      <div className="text-center mb-5 mt-4">
        <h2 className="fw-bold mb-2">{t('buy_credits.title')}</h2>
        <p className="text-muted" style={{ fontSize: 16 }}>{t('buy_credits.subtitle')}</p>
        
        <div className="mt-4 bg-light p-2 d-inline-flex rounded-pill gap-2 align-items-center">
          <button 
            className={`btn rounded-pill px-4 fw-semibold ${paymentMethod === 'card' ? 'btn-primary' : 'btn-light text-muted'}`}
            style={{ background: paymentMethod === 'card' ? '#fe5a1d' : 'transparent', border: 'none', fontSize: '15px', padding: '10px 24px' }}
            onClick={() => setPaymentMethod('card')}
          >
            <i className="bi bi-credit-card me-2"></i> {t('buy_credits.pay_with_card')}
          </button>
          <button 
            className={`btn rounded-pill px-4 fw-semibold ${paymentMethod === 'cash' ? 'btn-primary' : 'btn-light text-muted'}`}
            style={{ background: paymentMethod === 'cash' ? '#fe5a1d' : 'transparent', border: 'none', fontSize: '15px', padding: '10px 24px' }}
            onClick={() => setPaymentMethod('cash')}
          >
            <i className="bi bi-qr-code-scan me-2"></i> {t('buy_credits.pay_with_cash')}
          </button>
        </div>
      </div>

      <div className="row justify-content-center g-4">
        {packages.map((pkg, idx) => {
          const isPopular = idx === 1; // Highlighting the middle package usually
          return (
            <div key={pkg.id} className="col-lg-4 col-md-6">
              <div 
                className="dash-panel text-center h-100 position-relative shadow-sm"
                style={{ 
                  border: isPopular ? '2px solid #fe5a1d' : '1px solid var(--dash-border)',
                  transform: isPopular ? 'scale(1.03)' : 'none',
                  padding: '40px 24px'
                }}
              >
                {isPopular && (
                  <div className="position-absolute top-0 start-50 translate-middle badge rounded-pill" style={{ background: '#fe5a1d', padding: '6px 16px', fontSize: 12 }}>
                    {t('buy_credits.most_popular')}
                  </div>
                )}
                
                <h5 className="fw-bold text-muted mb-2 text-uppercase" style={{ letterSpacing: 1, fontSize: 14 }}>{pkg.label}</h5>
                <div className="d-flex justify-content-center align-items-center mb-4 mt-3">
                  <h1 className="fw-bold display-4 mb-0" style={{ color: '#1e293b' }}>{pkg.credits}</h1>
                  <span className="text-muted ms-2 mt-auto pb-2">{t('buy_credits.credits_unit')}</span>
                </div>
                
                <div className="mb-4">
                  <span className="fs-5 fw-bold text-dark">{pkg.price.toLocaleString()}</span>
                  <span className="text-muted ms-1">ALL</span>
                </div>

                <ul className="list-unstyled text-start text-muted mb-4" style={{ fontSize: 14 }}>
                  <li className="mb-2"><i className="bi bi-check-circle-fill text-success me-2"></i> {t('buy_credits.feature_instant')}</li>
                  <li className="mb-2"><i className="bi bi-check-circle-fill text-success me-2"></i> {t('buy_credits.feature_boost')}</li>
                  <li className="mb-2"><i className="bi bi-check-circle-fill text-success me-2"></i> {t('buy_credits.feature_support')}</li>
                </ul>

                <button 
                  className={`btn w-100 fw-bold py-2 ${isPopular ? '' : 'btn-light'}`}
                  style={{ 
                    background: isPopular ? '#fe5a1d' : '#f1f5f9',
                    color: isPopular ? '#fff' : '#1e293b',
                    border: 'none', borderRadius: 8 
                  }}
                  disabled={!!purchasing}
                  onClick={() => handlePurchase(pkg)}
                >
                  {purchasing === pkg.id ? <span className="spinner-border spinner-border-sm"></span> : t('buy_credits.btn_purchase')}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {qrData && (
        <div className="modal-backdrop fade show" style={{ zIndex: 1040 }} onClick={closeQrModal}>
          <div className="modal fade show d-block" style={{ zIndex: 1050 }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content shadow-lg border-0 rounded-4 p-4 text-center">
                <button type="button" className="btn-close position-absolute top-0 end-0 m-3" onClick={closeQrModal} />
                <h4 className="fw-bold mb-4">{t('buy_credits.modal_title')}</h4>
                <div className="bg-light p-4 rounded-4 mb-4 mx-auto" style={{ width: 'fit-content' }}>
                  {qrData.paymentCode ? <QRCodeSVG value={qrData.paymentCode} size={200} /> : <div className="text-muted">{t('buy_credits.loading_qr')}</div>}
                </div>
                <h3 className="fw-bold mb-1" style={{ color: '#fe5a1d', letterSpacing: 3 }}>{qrData.paymentCode}</h3>
                <p className="text-muted mb-4">{t('buy_credits.show_to_cashier')}</p>
                <div className="d-flex justify-content-between align-items-center bg-light rounded-2 p-3">
                  <span className="text-muted">{t('buy_credits.amount_due')}</span>
                  <span className="fw-bold fs-5">{Number(qrData.amount).toLocaleString()} L</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
